import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '@/lib/api';

export const useHTTPStream = (baseUrl: string, options: any = {}) => {
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const getStreamUrl = async (url: string) => {
    const cleanUrl = url.replace(/\/$/, '');
    const streamUrl = `${cleanUrl}/api/playground/stream`;
    
    // Get the auth token using the API client's method
    const isAuthenticated = await apiClient.checkAuth();
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }

    // Get the session to extract the token
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return `${streamUrl}?token=${encodeURIComponent(session.access_token)}`;
    }
    
    throw new Error('No access token available');
  };

  const connect = useCallback(async () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      setConnectionStatus('Connecting');
      const streamUrl = await getStreamUrl(baseUrl);
      console.log('🔌 Connecting to SSE stream with auth');
      
      const es = new EventSource(streamUrl);
      eventSourceRef.current = es;
      setEventSource(es);
      
      es.onopen = () => {
        setConnectionStatus('Connected');
        console.log('🔌 SSE stream connected');
        if (options.onOpen) options.onOpen();
      };
      
      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🔌 SSE message received:', data);
          if (options.onMessage) options.onMessage(data);
        } catch (error) {
          console.error('Failed to parse SSE message:', error);
        }
      };
      
      es.onerror = (error) => {
        setConnectionStatus('Error');
        console.error('🔌 SSE error:', error);
        if (options.onError) options.onError(error);
      };
      
    } catch (error) {
      setConnectionStatus('Error');
      console.error('🔌 Failed to create SSE connection:', error);
      if (options.onError) options.onError(error);
    }
  }, [baseUrl, options]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setEventSource(null);
      setConnectionStatus('Disconnected');
      console.log('🔌 SSE stream disconnected');
      if (options.onClose) options.onClose();
    }
  }, [options]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  return {
    connectionStatus,
    connect,
    disconnect,
    eventSource
  };
};